/**
 * EgressTopSources — Classement mensuel des sources d'egress Backblaze B2.
 *
 * Spec 2026-07-23 ext#4 : dans *Paramètres → Stockage*, affiche pour le mois
 * en cours (ou le mois choisi) la ventilation par source (backup_download,
 * doc_step_file, doc_field_file, task_chat_attachment) avec volume, % du
 * total et part attribuée du coût egress mensuel.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "@/lib/toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, TrendingDown, Files, Download, PackageOpen, FileText, Paperclip } from "lucide-react";
import api from "@/lib/api";

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  const u = ["o", "Ko", "Mo", "Go"];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 2 : 1)} ${u[i]}`;
};

const fmtUsd = (n) => (n === null || n === undefined) ? "—" : Number(n).toFixed(4);

const SOURCE_ICON = {
  backup_download:       PackageOpen,
  doc_step_file:         FileText,
  doc_field_file:        FileText,
  task_chat_attachment:  Paperclip,
};

const SOURCE_COLOR = {
  backup_download:       "#0078D4",
  doc_step_file:         "#8B5CF6",
  doc_field_file:        "#F5C842",
  task_chat_attachment:  "#22C55E",
};

export default function EgressTopSources() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/settings/storage/egress-sources");
      setData(data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Erreur inconnue";
      setError(msg);
      toast.error("Chargement impossible", { description: msg });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const monthLabel = useMemo(() => {
    if (!data?.month) return "";
    const [y, m] = data.month.split("-");
    try {
      const d = new Date(Number(y), Number(m) - 1, 1);
      return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    } catch { return data.month; }
  }, [data]);

  if (loading) {
    return (
      <Card className="p-6 bg-card border-vertec-borderDark text-sm text-muted-foreground" data-testid="egress-top-sources">
        Chargement du classement egress…
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card className="p-6 bg-card border-status-danger/40" data-testid="egress-top-sources">
        <div className="flex items-start gap-3">
          <div className="rounded-lg flex items-center justify-center shrink-0"
               style={{ width: 40, height: 40, background: "#141417", border: "1px solid #26262C", color: "#EF4444" }}>
            <TrendingDown size={20} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-status-danger mb-1">Classement egress indisponible</h3>
            <p className="text-xs text-muted-foreground">{error || "Aucune donnée."}</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 border-vertec-borderDark" data-testid="egress-sources-retry">
            <RefreshCw size={13} /> Réessayer
          </Button>
        </div>
      </Card>
    );
  }

  const sources = data.sources || [];
  const hasData = sources.length > 0 && data.total_bytes > 0;

  return (
    <Card className="p-6 bg-card border-vertec-borderDark" data-testid="egress-top-sources">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg flex items-center justify-center shrink-0"
               style={{ width: 40, height: 40, background: "#141417", border: "1px solid #26262C", color: "#EF4444" }}>
            <TrendingDown size={20} />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Top sources d&apos;egress</h3>
            <p className="text-xs text-muted-foreground max-w-2xl">
              Classement des flux sortants (téléchargements) par source pour <strong className="capitalize">{monthLabel}</strong>. Les parts $ sont réparties proportionnellement au volume, en s&apos;appuyant sur le coût egress total du mois.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5 border-vertec-borderDark" data-testid="egress-sources-reload">
          <RefreshCw size={13} /> Actualiser
        </Button>
      </div>

      {/* Résumé mensuel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="egress-sources-summary">
        <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Volume total</div>
          <div className="text-lg font-semibold tabular-nums mt-0.5" data-testid="egress-sources-total-bytes">{fmtBytes(data.total_bytes)}</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Événements</div>
          <div className="text-lg font-semibold tabular-nums mt-0.5" data-testid="egress-sources-total-events">{data.total_events}</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Coût egress</div>
          <div className="text-lg font-semibold tabular-nums mt-0.5" data-testid="egress-sources-cost">
            ≈ {fmtUsd(data.egress_usd_month)} $
            <span className="text-xs text-muted-foreground font-normal ml-1">· {fmtUsd(data.egress_eur_month)} €</span>
          </div>
        </div>
        <div className="rounded-lg p-3" style={{ background: "#17171B", border: "1px solid #26262C", borderLeft: "3px solid #22C55E" }}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Franchise gratuite (prorata)</div>
          <div className="text-lg font-semibold tabular-nums mt-0.5 text-status-success" data-testid="egress-sources-free">
            {(data.free_gb_prorata_month ?? 0).toFixed(3)} Go
          </div>
        </div>
      </div>

      {/* Tableau classement */}
      {hasData ? (
        <div className="rounded-lg overflow-hidden border border-vertec-borderDark" data-testid="egress-sources-table">
          <table className="w-full text-sm">
            <thead className="bg-vertec-borderDark/40 text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-right px-4 py-2 font-medium">Volume</th>
                <th className="text-right px-4 py-2 font-medium">Événements</th>
                <th className="text-right px-4 py-2 font-medium">% du volume</th>
                <th className="text-right px-4 py-2 font-medium">Part $</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s, idx) => {
                const Icon = SOURCE_ICON[s.source] || Download;
                const color = SOURCE_COLOR[s.source] || "#94a3b8";
                return (
                  <tr key={s.source}
                      className="border-t border-vertec-borderDark/40 hover:bg-white/[0.02] transition"
                      data-testid={`egress-source-row-${s.source}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center rounded"
                              style={{ width: 26, height: 26, background: "transparent", border: "1px solid #33333B", color }}>
                          <Icon size={13} />
                        </span>
                        <div>
                          <div className="text-sm font-medium">{s.label}</div>
                          <code className="text-[10px] text-muted-foreground">{s.source}</code>
                        </div>
                        {idx === 0 && (
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                                style={{ background: "transparent", border: "1px solid rgba(245,200,66,0.5)", color: "#F5C842" }}>
                            #1
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-right px-4 py-3 tabular-nums" data-testid={`egress-source-bytes-${s.source}`}>
                      {fmtBytes(s.bytes)}
                    </td>
                    <td className="text-right px-4 py-3 tabular-nums text-muted-foreground">
                      {s.events}
                    </td>
                    <td className="text-right px-4 py-3">
                      <div className="inline-flex flex-col items-end gap-1 min-w-[110px]">
                        <div className="tabular-nums text-xs" data-testid={`egress-source-pct-${s.source}`}>{s.pct_bytes.toFixed(1)} %</div>
                        <div className="w-24 h-1 rounded" style={{ background: "rgba(255,255,255,0.08)" }}>
                          <div className="h-1 rounded" style={{ width: `${Math.min(100, s.pct_bytes)}%`, background: color }} />
                        </div>
                      </div>
                    </td>
                    <td className="text-right px-4 py-3 tabular-nums" data-testid={`egress-source-share-${s.source}`}>
                      ≈ {fmtUsd(s.share_usd)} $
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-lg p-6 text-center text-sm text-muted-foreground"
             style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.10)" }}
             data-testid="egress-sources-empty">
          <Files size={22} className="mx-auto mb-2 opacity-40" />
          Aucun téléchargement enregistré ce mois-ci.
        </div>
      )}
    </Card>
  );
}
